import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Home", testId: "nav-home-link" },
  { to: "/services", label: "Services", testId: "nav-services-link" },
  { to: "/about", label: "About", testId: "nav-about-link" },
  { to: "/insights", label: "Insights", testId: "nav-insights-link" },
  { to: "/contact", label: "Contact", testId: "nav-contact-link" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed inset-x-0 top-0 z-50 border-b border-black/10 bg-white/70 backdrop-blur-xl"
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
          <Link
            to="/"
            data-testid="nav-logo-link"
            className="group flex items-baseline gap-2"
            aria-label="CalCor Solutions home"
          >
            <span className="inline-block h-2.5 w-2.5 translate-y-[-1px] rounded-[3px] bg-pine transition-colors duration-300 group-hover:bg-clay" />
            <span className="font-serif text-xl font-semibold tracking-tight text-ink">
              CalCor
            </span>
            <span className="text-sm font-medium tracking-wide text-zinc-500">Solutions</span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={cn(
                  "text-sm font-medium tracking-wide transition-colors duration-300 hover:text-clay",
                  pathname === item.to ? "text-pine" : "text-zinc-600"
                )}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <Link
              to="/portal"
              data-testid="header-client-portal-button"
              className="group inline-flex items-center gap-1.5 rounded-full border border-black/15 px-5 py-2.5 text-sm font-semibold text-ink transition-colors duration-300 hover:border-pine hover:text-pine"
            >
              Client Portal
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/contact#booking"
              data-testid="header-book-consultation-button"
              className="group inline-flex items-center gap-1.5 rounded-full bg-pine px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
            >
              Book a Consultation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/15 text-ink lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-paper px-8 lg:hidden"
          >
            <nav className="flex flex-col gap-6" aria-label="Mobile">
              {[...NAV, { to: "/portal", label: "Client Portal", testId: "mobile-nav-portal-link" }].map(
                (item, i) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <NavLink
                      to={item.to}
                      data-testid={item.testId}
                      onClick={() => setOpen(false)}
                      className="font-serif text-4xl tracking-tight text-ink transition-colors hover:text-pine"
                    >
                      {item.label}
                    </NavLink>
                  </motion.div>
                )
              )}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="pt-6"
              >
                <Link
                  to="/contact#booking"
                  data-testid="mobile-book-consultation-button"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-2 rounded-full bg-pine px-7 py-3.5 text-sm font-semibold text-white"
                >
                  Book a Consultation
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
